import React, { useCallback } from 'react'
import {
  Box,
  Button,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Select,
  Icon,
  useDisclosure,
  IconButton,
  Flex,
} from '@chakra-ui/react'
import { FaPlus, FaTrash } from 'react-icons/fa'
import { BatchImportModal } from './BatchImportModal'
import { FeatureDescriptionImportModal } from './FeatureDescriptionImportModal'

interface Feature {
  name: string
  format: string
  description: string
}

interface FeatureConfigProps {
  features: Feature[]
  onFeaturesChange: (features: Feature[]) => void
}

export const FeatureConfig: React.FC<FeatureConfigProps> = React.memo(({
  features,
  onFeaturesChange,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { 
    isOpen: isDescOpen, 
    onOpen: onDescOpen, 
    onClose: onDescClose 
  } = useDisclosure()

  const handleAddFeature = useCallback(() => {
    onFeaturesChange([...features, { name: 'default', format: 'double', description: '' }])
  }, [features, onFeaturesChange])

  const handleImportFeatures = useCallback((text: string) => {
    const newFeatures = text
      .split(',')
      .map((item) => ({
        name: item.trim(),
        format: 'double',
        description: '',
      }))
      .filter((item) => item.name)

    // Overwrite existing features instead of appending
    onFeaturesChange(newFeatures)
  }, [onFeaturesChange])

  const handleImportFeaturesWithDescription = useCallback((importedFeatures: Feature[]) => {
    onFeaturesChange(importedFeatures)
  }, [onFeaturesChange])

  const handleFeatureChange = useCallback((idx: number, field: keyof Feature, value: string) => {
    const newFeatures = features.map((f, i) => 
      i === idx ? { ...f, [field]: value } : f
    )
    onFeaturesChange(newFeatures)
  }, [features, onFeaturesChange])

  const handleRemoveFeature = useCallback((idx: number) => {
    onFeaturesChange(features.filter((_, i) => i !== idx))
  }, [features, onFeaturesChange])

  return (
    <Box borderWidth="1px" borderRadius="lg" p={4} bg="gray.50" position="relative">
      <Flex gap={4} alignItems="center" mb={4}>
        <Button
          leftIcon={<Icon as={FaPlus} />}
          onClick={handleAddFeature}
          size="md"
          variant="outline"
          colorScheme="blue"
          flexShrink={0}
        >
          Add Feature
        </Button>

        <Button
          rightIcon={<Icon as={FaPlus} />}
          onClick={onOpen}
          size="md"
          variant="outline"
          colorScheme="teal"
          flexShrink={0}
        >
          Batch Import
        </Button>

        <Button
          rightIcon={<Icon as={FaPlus} />}
          onClick={onDescOpen}
          size="md"
          variant="outline"
          colorScheme="purple"
          flexShrink={0}
        >
          Import with Description
        </Button>
      </Flex>

      {features.length > 0 && (
        <TableContainer bg="white" borderRadius="md" borderWidth="1px">
          <Table variant="simple" size="sm">
            <Thead bg="gray.100">
              <Tr>
                <Th>Feature Name</Th>
                <Th>Format</Th>
                <Th>Description</Th>
                <Th w="50px"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {features.map((feature, idx) => (
                <Tr key={idx}>
                  <Td>
                    <Input
                      size="sm"
                      value={feature.name}
                      placeholder="Feature name"
                      onChange={(e) =>
                        handleFeatureChange(idx, 'name', e.target.value)
                      }
                      borderRadius="md"
                      fontWeight="medium"
                    />
                  </Td>
                  <Td>
                    <Select
                      size="sm"
                      value={feature.format}
                      onChange={(e) =>
                        handleFeatureChange(idx, 'format', e.target.value)
                      }
                      borderRadius="md"
                    >
                      <option value="double">double</option>
                      <option value="int64">int64</option>
                    </Select>
                  </Td>
                  <Td>
                    <Input
                      size="sm"
                      value={feature.description}
                      placeholder="Feature description"
                      onChange={(e) =>
                        handleFeatureChange(idx, 'description', e.target.value)
                      }
                      borderRadius="md"
                    />
                  </Td>
                  <Td>
                    <IconButton
                      aria-label="Remove Feature"
                      icon={<FaTrash />}
                      size="sm"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => handleRemoveFeature(idx)}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      <BatchImportModal
        isOpen={isOpen}
        onClose={onClose}
        onImport={handleImportFeatures}
        title="Batch Import Features"
        placeholder="e.g. low,high,open,close"
      />

      <FeatureDescriptionImportModal
        isOpen={isDescOpen}
        onClose={onDescClose}
        onImport={handleImportFeaturesWithDescription}
      />
    </Box>
  )
})
